import { ContactInfo, Education, ResumeSectionType, SkillCategory, WorkExperienceCompany } from '@applicator/shared'
import React from 'react'

import type { AccountSummaryVariant } from '../../lib/accountProfessionalSummaryLibrary'
import type { AccountTargetTitleVariant } from '../../lib/accountTargetTitleLibrary'
import type { TemplateResumeSummary } from '../../lib/resumeTemplates'
import { isEducationCatalogEqual } from '../../lib/accountEducation'
import { isSkillsCatalogEqual } from '../../lib/accountSkills'
import {
  type PendingSectionSaveRef,
  saveResumeSectionNow,
  scheduleDebouncedResumeSectionSave,
} from '../../utils/resumeSectionSaveScheduler'
import { isResumeSectionListEmpty } from '../../utils/resumeSectionEmptyHint'

import CertificationsEditor from './CertificationsEditor'
import ContactInfoEditor from './ContactInfoEditor'
import EducationEditor from './EducationEditor'
import InterestsEditor from './InterestsEditor'
import ProfessionalSummaryEditor from './ProfessionalSummaryEditor'
import ProjectsEditor from './ProjectsEditor'
import SkillsEditor from './SkillsEditor'
import TargetTitleEditor from './TargetTitleEditor'
import WorkExperienceEditor from './WorkExperienceEditor'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'

interface ResumeSectionEditorProps {
  sectionType: ResumeSectionType
  resumeId: string | undefined
  getActiveResumeId: () => string | undefined
  data: Record<string, unknown>
  resumeData: Record<ResumeSectionType, Record<string, unknown>>
  saveTimeoutRef: React.MutableRefObject<Record<ResumeSectionType, NodeJS.Timeout | null>>
  pendingSectionSaveRef?: PendingSectionSaveRef
  onUpdateResumeData: (updater: (prev: Record<ResumeSectionType, Record<string, unknown>>) => Record<ResumeSectionType, Record<string, unknown>>) => void
  onSaveResumeContent: (sectionType: ResumeSectionType, content: Record<string, unknown>, skipStateUpdate?: boolean) => void
  onUpdateContactInfo: (field: keyof ContactInfo, value: ContactInfo[keyof ContactInfo]) => void
  addSummaryEntryRef: React.MutableRefObject<(() => void) | null>
  addWorkExperienceRef: React.MutableRefObject<(() => void) | null>
  addEducationRef: React.MutableRefObject<(() => void) | null>
  addRoleRef: React.MutableRefObject<(() => void) | null>
  addTargetTitleRef: React.MutableRefObject<(() => void) | null>
  addContactExtraFieldRef: React.MutableRefObject<(() => void) | null>
  addCertificationRef: React.MutableRefObject<(() => void) | null>
  addProjectRef?: React.MutableRefObject<(() => void) | null>
  addInterestRef?: React.MutableRefObject<(() => void) | null>
  highlightedRoleId: string | null
  highlightedBulletId?: string | null
  highlightedTargetTitleId: string | null
  highlightedSummaryId: string | null
  onWorkExperienceChange?: (newData: WorkExperienceCompany[]) => void
  skillsScrollRootRef?: React.RefObject<HTMLElement | null>
  onActiveSkillCategoryChange?: (categoryName: string | null) => void
  accountSummaryVariants?: AccountSummaryVariant[]
  accountSummaryVariantsLoading?: boolean
  accountTargetTitleVariants?: AccountTargetTitleVariant[]
  accountTargetTitleVariantsLoading?: boolean
  onFocusEducationEntry?: (educationId: string) => void
  isTemplateResume?: boolean
  templateResumes?: TemplateResumeSummary[]
  fetchTemplateSkillsForResume?: (templateResumeId: string) => Promise<SkillCategory[] | null>
}

export default function ResumeSectionEditor({
  sectionType,
  resumeId,
  getActiveResumeId,
  data,
  resumeData: _resumeData,
  saveTimeoutRef,
  pendingSectionSaveRef,
  onUpdateResumeData,
  onSaveResumeContent,
  onUpdateContactInfo,
  addSummaryEntryRef,
  addWorkExperienceRef,
  addEducationRef,
  addRoleRef,
  addTargetTitleRef,
  addContactExtraFieldRef,
  addCertificationRef,
  addProjectRef,
  addInterestRef,
  highlightedRoleId,
  highlightedBulletId,
  highlightedTargetTitleId,
  highlightedSummaryId,
  onWorkExperienceChange,
  skillsScrollRootRef,
  onActiveSkillCategoryChange,
  accountSummaryVariants,
  accountSummaryVariantsLoading,
  accountTargetTitleVariants,
  accountTargetTitleVariantsLoading,
  onFocusEducationEntry,
  isTemplateResume,
  templateResumes,
  fetchTemplateSkillsForResume,
}: ResumeSectionEditorProps) {
  switch (sectionType) {
    case 'contact_info':
      return (
        <ContactInfoEditor
          data={data}
          onChange={onUpdateContactInfo}
          onAddExtraFieldRef={(fn) => {
            addContactExtraFieldRef.current = fn
          }}
        />
      )
    
    case 'target_title':
      return (
        <TargetTitleEditor
          data={Array.isArray(data) ? data : []}
          onChange={(newData) => {
            onUpdateResumeData((prev) => ({ ...prev, target_title: newData }))
            scheduleDebouncedResumeSectionSave(
              saveTimeoutRef,
              'target_title',
              resumeId,
              getActiveResumeId,
              newData,
              onSaveResumeContent,
              undefined,
              pendingSectionSaveRef,
            )
          }}
          onAddTitleRef={(fn) => {
            addTargetTitleRef.current = fn
          }}
          highlightedTitleId={highlightedTargetTitleId}
          accountTitleVariants={accountTargetTitleVariants}
          accountTitleVariantsLoading={accountTargetTitleVariantsLoading}
        />
      )
    
    case 'professional_summary':
      return (
        <ProfessionalSummaryEditor
          data={Array.isArray(data) ? data : []}
          onChange={(newData) => {
            onUpdateResumeData((prev) => ({ ...prev, professional_summary: newData }))
            scheduleDebouncedResumeSectionSave(
              saveTimeoutRef,
              'professional_summary',
              resumeId,
              getActiveResumeId,
              newData,
              onSaveResumeContent,
              undefined,
              pendingSectionSaveRef,
            )
          }}
          onAddEntryRef={(fn) => {
            addSummaryEntryRef.current = fn
          }}
          highlightedSummaryId={highlightedSummaryId}
          accountVariants={accountSummaryVariants}
          accountVariantsLoading={accountSummaryVariantsLoading}
        />
      )
    
    case 'work_experience':
      return (
        <WorkExperienceEditor
          data={data}
          onChange={(newData) => {
            if (onWorkExperienceChange) {
              onWorkExperienceChange(newData)
              return
            }
            onUpdateResumeData((prev) => ({ ...prev, work_experience: newData }))
            scheduleDebouncedResumeSectionSave(
              saveTimeoutRef,
              'work_experience',
              resumeId,
              getActiveResumeId,
              newData,
              onSaveResumeContent,
              undefined,
              pendingSectionSaveRef,
            )
          }}
          onAddCompanyRef={(fn) => {
            addWorkExperienceRef.current = fn
          }}
          onAddRoleRef={(fn) => {
            addRoleRef.current = fn
          }}
          highlightedRoleId={highlightedRoleId}
          highlightedBulletId={highlightedBulletId}
        />
      )
    
    case 'education':
      return (
        <EducationEditor
          data={data}
          onChange={(newData) => {
            const prevEducation = Array.isArray(data) ? (data as Education[]) : []
            onUpdateResumeData((prev) => ({ ...prev, education: newData }))
            const catalogChanged = !isEducationCatalogEqual(prevEducation, newData)
            if (catalogChanged) {
              saveResumeSectionNow(
                saveTimeoutRef,
                pendingSectionSaveRef,
                'education',
                resumeId,
                getActiveResumeId,
                newData,
                onSaveResumeContent,
              )
            } else {
              scheduleDebouncedResumeSectionSave(
                saveTimeoutRef,
                'education',
                resumeId,
                getActiveResumeId,
                newData,
                onSaveResumeContent,
                undefined,
                pendingSectionSaveRef,
              )
            }
          }}
          onAddEducationRef={(fn) => {
            addEducationRef.current = fn
          }}
          onFocusEducationEntry={onFocusEducationEntry}
        />
      )
    
    case 'skills':
      return (
        <SkillsEditor
          data={data as SkillCategory[]}
          onChange={(newData) => {
            const prevSkills = Array.isArray(data) ? (data as SkillCategory[]) : []
            onUpdateResumeData((prev) => ({ ...prev, skills: newData }))
            const catalogChanged = !isSkillsCatalogEqual(prevSkills, newData)
            if (catalogChanged) {
              saveResumeSectionNow(
                saveTimeoutRef,
                pendingSectionSaveRef,
                'skills',
                resumeId,
                getActiveResumeId,
                newData,
                onSaveResumeContent,
              )
            } else {
              scheduleDebouncedResumeSectionSave(
                saveTimeoutRef,
                'skills',
                resumeId,
                getActiveResumeId,
                newData,
                onSaveResumeContent,
                undefined,
                pendingSectionSaveRef,
              )
            }
          }}
          scrollRootRef={skillsScrollRootRef}
          onActiveCategoryChange={onActiveSkillCategoryChange}
          isTemplateResume={isTemplateResume}
          templateResumes={templateResumes}
          fetchTemplateSkillsForResume={fetchTemplateSkillsForResume}
        />
      )
    
    case 'interests':
      return (
        <InterestsEditor
          data={data}
          onChange={(newData) => onSaveResumeContent('interests', newData)}
          onAddInterestRef={(fn) => {
            if (addInterestRef) addInterestRef.current = fn
          }}
        />
      )
    
    case 'certifications':
      return (
        <CertificationsEditor
          data={data}
          onChange={(newData) => onSaveResumeContent('certifications', newData)}
          onAddCertificationRef={(fn) => {
            addCertificationRef.current = fn
          }}
        />
      )
    
    case 'projects':
      return (
        <ProjectsEditor
          data={Array.isArray(data) ? data : []}
          onChange={(newData) => {
            onUpdateResumeData((prev) => ({ ...prev, projects: newData }))
            scheduleDebouncedResumeSectionSave(
              saveTimeoutRef,
              'projects',
              resumeId,
              getActiveResumeId,
              newData,
              onSaveResumeContent,
              undefined,
              pendingSectionSaveRef,
            )
          }}
          onAddProjectRef={(fn) => {
            if (addProjectRef) {
              addProjectRef.current = fn
            }
          }}
        />
      )
    
    default:
      if (isResumeSectionListEmpty(data)) {
        return <ResumeSectionEmptyHint sectionType={sectionType} />
      }
      return null
  }
}
